/**
 * "You're out of credits" dialog: offers the first two credit packs.
 *
 * The dialog now routes straight to the real top-up flow, where JVZoo checkout takes over.
 */
import { useState } from 'react';
import { Coins as CoinsIcon, X } from 'lucide-react';
import { Button, Modal } from '../ui';
import { RadioCard, RadioCardGroup } from '../forms';
import { FRONTEND_PRODUCT, PACKS } from '../../data/products';
import { c } from '../../styles/theme';

/** The two packs offered inside the dialog — the full list lives on /topup. */
const OFFERED = PACKS.slice(0, 2);

export interface OutOfCreditsModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the chosen pack id. */
  onBuy: (packId: string) => void;
  /** Initially selected pack. Defaults to the app's default pack. */
  defaultPackId?: string;
}

export default function OutOfCreditsModal({
  open,
  onClose,
  onBuy,
  defaultPackId = 'p200',
}: OutOfCreditsModalProps) {
  const [selected, setSelected] = useState(defaultPackId);
  const pack = PACKS.find((p) => p.id === selected);

  return (
    <Modal open={open} onClose={onClose} ariaLabel="You are out of credits">
      <button
        type="button"
        onClick={onClose}
        style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >
        <X size={16} color={c.text2} />
      </button>
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: c.dangerSoft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12,
        }}
      >
        <CoinsIcon size={20} color={c.danger} />
      </div>
      <p style={{ fontWeight: 600, fontSize: 15, margin: '0 0 4px', fontFamily: 'Space Grotesk' }}>
        You're out of credits
      </p>
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 14px', lineHeight: 1.5 }}>
        Your {FRONTEND_PRODUCT.name} credits do not reset monthly. Buy more credits to keep creating.
      </p>

      <RadioCardGroup label="Credit pack" gap={6} marginBottom={12}>
        {OFFERED.map((p) => (
          <RadioCard
            key={p.id}
            name="lyx-credit-pack"
            value={p.id}
            checked={selected === p.id}
            onSelect={setSelected}
            size="compact"
            ariaLabel={`${p.credits} credits for $${p.price}`}
            trailing={<span style={{ fontSize: 12, fontWeight: 500 }}>${p.price}</span>}
          >
            <span style={{ fontSize: 12 }}>
              {p.credits} credits
              {p.best && <span style={{ color: c.accent }}> · popular</span>}
            </span>
          </RadioCard>
        ))}
      </RadioCardGroup>

      <Button full onClick={() => onBuy(selected)} style={{ marginBottom: 8 }}>
        Buy {pack?.credits} credits — ${pack?.price}
      </Button>
      <Button full variant="ghost" onClick={onClose}>
        Not now
      </Button>
    </Modal>
  );
}
